import "./index.css";

function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12">
      <h2 className="font-nav text-base md:text-lg tracking-[0.2em] uppercase text-[#c44a1f] mb-5">
        <span className="text-[#f5f1e6]/40 mr-3">{number}.</span>
        {title}
      </h2>
      <div className="space-y-4 text-[#f5f1e6]/85 leading-relaxed text-[15px] md:text-base">{children}</div>
    </section>
  );
}

export function PrivacyPolicy() {
  return (
    <main className="min-h-screen bg-black text-[#f5f1e6] font-serif">
      <header className="border-b border-[#c44a1f]/20">
        <div className="mx-auto max-w-3xl px-6 md:px-12 py-6 flex items-center justify-between">
          <a href="/" className="font-gothic text-xl md:text-2xl text-[#f5f1e6] hover:text-[#c44a1f] transition-colors">
            <span className="font-flourish text-[#c44a1f]">Z</span>eit
            <span className="font-flourish text-[#c44a1f]">F</span>orge
          </a>
          <a href="/" className="font-nav text-[10px] md:text-xs tracking-[0.35em] uppercase text-[#f5f1e6]/60 hover:text-[#c44a1f] transition-colors">
            ← Home
          </a>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-6 md:px-12 py-16 md:py-20">
        <h1 className="gothic-glow font-gothic text-4xl md:text-6xl leading-tight mb-3">
          <span className="font-flourish text-[#c44a1f]">P</span>rivacy{" "}
          <span className="font-flourish text-[#c44a1f]">P</span>olicy
        </h1>
        <p className="font-nav text-[10px] md:text-xs tracking-[0.4em] uppercase text-[#c44a1f]/80 mb-10">
          for World Cup Predictor
        </p>
        <p className="text-sm text-[#f5f1e6]/70">
          <span className="text-[#c44a1f] uppercase tracking-widest text-xs font-nav mr-2">Effective Date</span>
          May 28, 2026
        </p>

        <Section number="1" title="Introduction">
          <p>
            ZEITFORGE (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) is committed to protecting your
            privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when
            you use the World Cup Predictor mobile application (the &ldquo;App&rdquo;). By downloading and using the
            App, you agree to the terms of this Privacy Policy.
          </p>
        </Section>

        <Section number="2" title="Information We Collect">
          <p>To provide a customized and functional experience, we collect specific information when you use our App.</p>
          <p className="font-nav text-xs tracking-[0.25em] uppercase text-[#c44a1f]/80 mt-6 mb-2">
            Data Collection Summary
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[#c44a1f]/40">
                  <th className="text-left py-3 pr-3 font-nav text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#c44a1f]">
                    Data Type
                  </th>
                  <th className="text-left py-3 px-3 font-nav text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#c44a1f]">
                    Purpose
                  </th>
                  <th className="text-left py-3 pl-3 font-nav text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#c44a1f]">
                    Stored or Transmitted
                  </th>
                </tr>
              </thead>
              <tbody className="text-[#f5f1e6]/85">
                <tr className="border-b border-[#c44a1f]/10">
                  <td className="py-3 pr-3 font-semibold">Full Name</td>
                  <td className="py-3 px-3">Account management, sign-in, personalization</td>
                  <td className="py-3 pl-3">Transmitted off-device (encrypted)</td>
                </tr>
                <tr className="border-b border-[#c44a1f]/10">
                  <td className="py-3 pr-3 font-semibold">Email Address</td>
                  <td className="py-3 px-3">Account management, sign-in, developer communications</td>
                  <td className="py-3 pl-3">Transmitted off-device (encrypted)</td>
                </tr>
                <tr className="border-b border-[#c44a1f]/10">
                  <td className="py-3 pr-3 font-semibold">App Interactions</td>
                  <td className="py-3 px-3">Analytics, advertising, app functionality</td>
                  <td className="py-3 pl-3">Transmitted to third parties (analytics / ads)</td>
                </tr>
                <tr>
                  <td className="py-3 pr-3 font-semibold">Device IDs</td>
                  <td className="py-3 px-3">Analytics, advertising, fraud prevention</td>
                  <td className="py-3 pl-3">Transmitted to third parties (analytics / ads)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section number="3" title="How We Use Your Information">
          <p>We only use your collected data for the following legitimate business purposes:</p>
          <ul className="list-disc pl-6 space-y-2 marker:text-[#c44a1f]">
            <li>To create and manage your World Cup Predictor account.</li>
            <li>To authenticate your identity during the sign-in process.</li>
            <li>To deliver personalized and non-personalized advertisements to keep the app free.</li>
            <li>To monitor and analyze usage patterns via Google Analytics to improve app performance and user experience.</li>
            <li>To respond to your customer service inquiries or support requests.</li>
          </ul>
        </Section>

        <Section number="4" title="Third-Party Services and Data Sharing">
          <p>
            We do not sell your personal information. We share your data only with specific third-party service
            providers who assist us in operating our App:
          </p>
          <ul className="list-disc pl-6 space-y-2 marker:text-[#c44a1f]">
            <li>
              <strong className="text-[#f5f1e6]">Google Analytics:</strong> Used to track app usage, crashes, and
              performance metrics.
            </li>
            <li>
              <strong className="text-[#f5f1e6]">Advertising Networks:</strong> Used to serve ads within the app.
              These networks may collect Device IDs and app interaction data to provide relevant advertising.
            </li>
          </ul>
          <p className="italic text-[#f5f1e6]/70">
            Both Google Analytics and our advertising partners process your data in accordance with their own privacy
            policies.
          </p>
        </Section>

        <Section number="5" title="Data Security and Encryption">
          <p>
            We use administrative, technical, and physical security measures to help protect your personal
            information. In compliance with Google Play standards, all personal data (Name, Email) transmitted between
            your device and our servers is encrypted in transit using HTTPS / TLS protocols.
          </p>
        </Section>

        <Section number="6" title="Data Retention and Deletion">
          <p>
            We retain your personal information only for as long as your account is active or as needed to provide you
            with the App&rsquo;s services.
          </p>
          <p className="font-nav text-xs tracking-[0.25em] uppercase text-[#c44a1f]/80 mt-6 mb-1">
            Your Right to Delete Data
          </p>
          <p>
            In accordance with Google Play&rsquo;s User Data policy, you have the right to request the deletion of
            your account and associated personal data. You can request data deletion directly within the App&rsquo;s
            settings menu or by contacting us using the information below. Once requested, your name and email
            address will be permanently removed from our active databases.
          </p>
        </Section>

        <Section number="7" title="Children's Privacy">
          <p>
            Our App does not knowingly collect personal information from children under the age of 13 (or the
            applicable age of consent in your region). If we learn that we have collected personal data from a child
            without verified parental consent, we will delete that information immediately.
          </p>
        </Section>

        <Section number="8" title="Contact Us">
          <p>If you have questions, concerns, or wish to exercise your data rights, please contact us at:</p>
          <dl className="mt-4 space-y-2">
            <div className="flex gap-3">
              <dt className="font-nav text-xs tracking-[0.25em] uppercase text-[#c44a1f]/80 w-24 shrink-0 pt-0.5">
                Company
              </dt>
              <dd>ZEITFORGE</dd>
            </div>
            <div className="flex gap-3">
              <dt className="font-nav text-xs tracking-[0.25em] uppercase text-[#c44a1f]/80 w-24 shrink-0 pt-0.5">
                Address
              </dt>
              <dd>5 Mayville Road, Ilford, England, IG1 2HU</dd>
            </div>
            <div className="flex gap-3">
              <dt className="font-nav text-xs tracking-[0.25em] uppercase text-[#c44a1f]/80 w-24 shrink-0 pt-0.5">
                Email
              </dt>
              <dd>
                <a
                  href="mailto:info@zeitforge.com"
                  className="text-[#f5f1e6] hover:text-[#c44a1f] transition-colors"
                >
                  info@zeitforge.com
                </a>
              </dd>
            </div>
          </dl>
        </Section>
      </article>

      <footer className="border-t border-[#c44a1f]/20 mt-16">
        <div className="mx-auto max-w-3xl px-6 md:px-12 py-8 flex items-center justify-between">
          <span className="font-nav text-[10px] md:text-xs tracking-[0.4em] uppercase text-[#f5f1e6]/50">
            ZeitForge · MMXXVI
          </span>
          <a
            href="/"
            className="font-nav text-[10px] md:text-xs tracking-[0.35em] uppercase text-[#f5f1e6]/60 hover:text-[#c44a1f] transition-colors"
          >
            Return to Forge
          </a>
        </div>
      </footer>
    </main>
  );
}

export default PrivacyPolicy;
